import {Component, Injector, OnInit} from '@angular/core';
import {BaseComponent} from "../../base.component";
import {ListResponse} from "../../../network/service/list.response";
import {SnackBarUtil} from "../../../util/snack.bar.util";
import {Params} from "@angular/router";
import {FlatTreeControl} from "@angular/cdk/tree";
import {MatTreeFlatDataSource, MatTreeFlattener} from "@angular/material/tree";
import {DataResponse} from "../../../network/service/data.response";
import {RequestType} from "../../../enums/request.type";

interface Permission {
  PermissionId: string;
  Title: string;
  Children?: Permission[];
  expandable: boolean;
  level: number;
}

@Component({
  selector: 'app-role-permissions',
  standalone: false,
  templateUrl: './role-permissions.component.html',
  styleUrls: ['./role-permissions.component.scss']
})
export class RolePermissionsComponent extends BaseComponent implements OnInit {
  businessId?: any;
  roleId?: any;
  roleTitle?: any;
  userPermissions?: any = [];
  selectAllChecked = false;

  constructor(injector: Injector) {
    super(injector);
    this.activatedRoute.params.subscribe((params: Params) => {
      if (params['BusinessId'] && params['BusinessId'] !== '') {
        this.businessId = params['BusinessId'];
      }
      if (params['RoleId'] && params['RoleId'] !== '') {
        this.roleId = params['RoleId'];
      }
      if (params['RoleTitle'] && params['RoleTitle'] !== '') {
        this.roleTitle = params['RoleTitle'];
      }
    });
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  override loadOnline() {
    this.serviceApi.getMyPermissions(undefined, undefined, undefined, RequestType.ALL, undefined).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        this.dataSource.data = this.buildTree(res.data!, null);
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
    this.serviceApi.getRolesPermissions(undefined, undefined, undefined, RequestType.ALL, {
      Filter: {
        Type: 'AND',
        Values: [
          {
            Field: 'BusinessId',
            Value: this.businessId
          }, {
            Field: 'RoleId',
            Value: this.roleId
          }
        ]
      }
    }).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        this.userPermissions = res.data?.map((userPermission: any) => {
          return userPermission.PermissionId;
        });
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  onAddPermissionToRole() {
    this.serviceApi.createRolesPermissions({
      RoleId: this.roleId,
      PermissionIds: this.userPermissions,
      BusinessId: this.businessId
    }).subscribe((res: DataResponse<any>) => {
      if (res.success) {
        this.loadOnline();
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  selectAllNodes() {
    this.selectAllChecked = !this.selectAllChecked;
    this.selectAllNodesRecursive(this.dataSource.data, this.selectAllChecked);
  }

  selectAllNodesRecursive(nodes: any[], checked: boolean) {
    for (let node of nodes) {
      this.addToUserPermissions(node, {checked});
      if (node.Children) {
        this.selectAllNodesRecursive(node.Children, checked);
      }
    }
  }

  buildTree(data: any[], parentId: string | null): [] {
    const tree: any = [];
    for (let node of data) {
      if (node.ParentId === parentId) {
        const treeNode: any = {
          ...node,
          Children: this.buildTree(data, node.PermissionId),
        };
        tree.push(treeNode);
      }
    }
    return tree;
  }

  isUserPermission(node: Permission): boolean {
    return this.userPermissions.some((userPermission: any) => {
      return userPermission === node.PermissionId;
    });
  }

  addToUserPermissions(node: Permission, event: any): void {
    if (event.checked) {
      this.userPermissions.push(node.PermissionId);
      this.findChildrenRecursive(this.findNodeById(this.dataSource.data, node.PermissionId), true);
    } else {
      let permissionIndex = this.userPermissions.indexOf(node.PermissionId);
      this.userPermissions.splice(permissionIndex, 1);
      this.findChildrenRecursive(this.findNodeById(this.dataSource.data, node.PermissionId), false);
    }
  }

  findNodeById(permissions: Permission[], permissionId: string): Permission | null {
    for (const permission of permissions) {
      if (permission.PermissionId === permissionId) {
        return permission;
      }
      if (permission.Children) {
        const found = this.findNodeById(permission.Children, permissionId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  findChildrenRecursive(permission: any, status: boolean) {
    if (permission.Children) {
      for (let i = 0; i < permission.Children.length; i++) {
        if (status) {
          this.userPermissions.push(permission.Children[i].PermissionId);
        } else {
          this.userPermissions = this.userPermissions.filter((item: any) => {
            return item !== permission.Children[i].PermissionId;
          });
        }
        this.findChildrenRecursive(permission.Children[i], status);
      }
    }
  }

  hasChild = (_: number, node: Permission) => node.expandable;

  private _transformer = (node: Permission, level: number) => {
    return {
      expandable: !!node.Children && node.Children.length > 0,
      PermissionId: node.PermissionId,
      Title: node.Title,
      level: level,
    };
  };
  treeControl = new FlatTreeControl<Permission>(
    (node) => node.level,
    (node) => node.expandable
  );
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.Children
  );
  dataSource: any = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
}
